import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LifeBuoy, Search, ChevronRight, Target, ClipboardCheck, MessageSquareQuote, Sparkles, Grid3x3, Crown, ChartBar as BarChart, KeyRound, CreditCard, Users, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";

const ARTICLES = [
  {
    id: "getting-started", category: "Getting started", icon: Target,
    title: "What Axia is — and isn't",
    body: `Axia is your team's performance home: goals, check-ins, reviews, kudos, calibration, and AI assistance — all in one workspace.

We **never** store compensation data. No salary, bonus, equity, or pay information is accepted at any boundary (CSV import, API, custom fields). Compensation lives in your HRIS or payroll system.`,
  },
  {
    id: "first-week", category: "Getting started", icon: Users,
    title: "Your first week as a tenant admin",
    body: `1. **Bulk-import employees** — Admin → Bulk import. Upload a CSV with employee_id, name, email, manager. We'll surface cycle/orphan errors before commit.
2. **Invite users** — Admin → Users. Send magic-link invites; recipients pick their password-free OTP login on first sign-in.
3. **Pick a framework** — Admin → Frameworks. Three are pre-published (Balanced Scorecard, SMART + Values, Classic OKR). Clone-to-edit.
4. **Activate a cycle** — Admin → Cycles. Activation auto-routes reviewers from your org chart and creates participant records.
5. **Tell people** — share /goals as the first place every employee should land.`,
  },
  {
    id: "goals-flow", category: "Goals", icon: Target,
    title: "How goal-setting works",
    body: `Employees draft objectives → submit when weightages sum to 100 → manager approves or requests changes → both clickwrap-sign → the objective locks. Edits after lock require an amendment.

Use **SMART Coach** in the editor for an AI critique + rewrite, and **Suggest metrics** to populate the measurement field.`,
  },
  {
    id: "reviews-flow", category: "Reviews", icon: ClipboardCheck,
    title: "Mid-year and year-end reviews",
    body: `Reviews are spawned automatically when HR opens the corresponding cycle phase. Flow: self_pending → manager_pending → joint_pending → signed_off.

The **Draft with AI** button pulls from check-ins, evidence and kudos to compose a 3-paragraph draft. **Bias Lens** flags gendered or vague language with neutral rewrites. **Quality Score** audits specificity & balance.`,
  },
  {
    id: "calibration", category: "HR", icon: Grid3x3,
    title: "Running a calibration session",
    body: `HR creates a session for a cycle/phase. Participants are auto-populated from existing reviews. Drag chips between 9-box cells (Performance × Potential), edit proposed ratings, flag HiPo. **Commit** locks the session, writes to performance_history, and fires HiPo lifecycle events.

Use **AI Insights** to surface manager-level rating bias, distribution skews, and HiPo concentration concerns — without naming individual employees.`,
  },
  {
    id: "succession", category: "HR", icon: Crown,
    title: "Succession & 9-box snapshot",
    body: `The Succession page is a read-only roll-up of the latest committed calibration session per employee. Use it to identify HiPo bench depth and successor coverage for key leadership roles.`,
  },
  {
    id: "feedback-360", category: "Feedback", icon: MessageSquareQuote,
    title: "Anonymous 360 / peer feedback",
    body: `Anyone can request peer feedback for themselves; managers can request on behalf of their reports; HR for anyone. Default is **anonymous** — peer identity is redacted on read. Use **Extract themes** to aggregate qualitative responses without revealing identities.`,
  },
  {
    id: "ai", category: "AI", icon: Sparkles,
    title: "AI features and your data",
    body: `All AI features run via Claude Sonnet 4.5 through the Emergent universal key. Prompts include explicit guardrails forbidding compensation discussion and emoji.

Endpoints: /api/v1/ai/{smart-coach, metric-suggester, summary, bias-lens, themes, review-quality, calibration-insights}. Every AI call is logged in audit_events.`,
  },
  {
    id: "platform", category: "Developers", icon: KeyRound,
    title: "Public API & webhooks",
    body: `Issue API tokens at Admin → Platform. Tokens are bearer-style (\`axia_pat_...\`) and shown exactly once.

**Public read API:** GET /api/public/v1/{employees, objectives, reviews, cycles}.
**Webhooks:** register a URL + event list. Each delivery is HMAC-SHA256 signed in the \`X-Axia-Signature\` header — verify using the per-webhook secret you got at registration.

Events: \`review.signed_off\`, \`review.calibrated\`, \`kudos.created\`, \`objective.approved\`, \`objective.locked\`, \`calibration.committed\`, \`cycle.activated\`.`,
  },
  {
    id: "billing", category: "Billing", icon: CreditCard,
    title: "How billing works",
    body: `Per-seat pricing — one seat per active employee in your tenant. Tenant admins see seat count + monthly total at Admin → Billing and pay via Stripe Checkout. Each successful payment activates a 30-day billing period. We never store card data.`,
  },
  {
    id: "compensation-guard", category: "Privacy", icon: ShieldCheck,
    title: "The compensation guard",
    body: `Axia rejects any field that looks like compensation — \`salary\`, \`bonus\`, \`ctc\`, \`stipend\`, \`pay_grade\`, \`commission\`, etc. — at every boundary: CSV imports, API payloads, AI requests. We are a performance product, not a payroll product.`,
  },
];

const CATEGORIES = Array.from(new Set(ARTICLES.map((a) => a.category)));

export default function HelpCenter() {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(ARTICLES[0]);
  const nav = useNavigate();
  const filtered = useMemo(() => {
    if (!q.trim()) return ARTICLES;
    const s = q.toLowerCase();
    return ARTICLES.filter((a) => a.title.toLowerCase().includes(s) || a.body.toLowerCase().includes(s) || a.category.toLowerCase().includes(s));
  }, [q]);

  return (
    <div className="max-w-6xl space-y-6" data-testid="help-page">
      <div>
        <div className="axia-overline">Help</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">How can we help?</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-xl">Quick guides covering goals, reviews, calibration, AI, the developer platform, and privacy.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-10" placeholder="Search articles…" data-testid="help-search" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="axia-card p-3 space-y-1 max-h-[60vh] overflow-y-auto" data-testid="help-sidebar">
          {CATEGORIES.map((cat) => {
            const items = filtered.filter((a) => a.category === cat);
            if (items.length === 0) return null;
            return (
              <div key={cat} className="pt-2 first:pt-0">
                <div className="axia-overline px-2 pb-1.5">{cat}</div>
                {items.map((a) => {
                  const Icon = a.icon;
                  const isActive = active.id === a.id;
                  return (
                    <button key={a.id} onClick={() => setActive(a)} className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left text-sm transition ${isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`help-link-${a.id}`}>
                      <Icon className={`w-3.5 h-3.5 ${isActive ? "text-slate-900" : "text-slate-400"}`} />
                      <span className="truncate">{a.title}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </aside>
        <article className="axia-card p-8" data-testid="help-article">
          <div className="axia-overline">{active.category}</div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-1.5">{active.title}</h2>
          <div className="prose prose-slate max-w-none mt-4 text-sm text-slate-700 whitespace-pre-line leading-relaxed">{active.body}</div>
          <div className="mt-8 pt-4 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-2">
            <LifeBuoy className="w-3.5 h-3.5" />
            Still stuck? <button onClick={() => nav("/notifications")} className="underline hover:text-slate-900">Ping HR via Notifications</button>.
          </div>
        </article>
      </div>
    </div>
  );
}
